import React, { Component } from 'react';
import { Box, Typography, Button, Paper, Alert, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';

class EnhancedErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
    
    // In production, you could send this to an error reporting service
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const isNetworkError = error?.message?.includes('Network') || error?.message?.includes('fetch');
      const isDataError = error?.message?.includes('map') || error?.message?.includes('filter');
      
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          p: 4 
        }}>
          <Paper elevation={4} sx={{ p: 4, maxWidth: 500, textAlign: 'center', borderRadius: 3 }}>
            <BugReportIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              Oops! Something went wrong
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {isNetworkError && "Network connection issue. Please check your internet connection."}
              {isDataError && "Data loading issue. The server may be temporarily unavailable."}
              {!isNetworkError && !isDataError && "An unexpected error occurred."}
            </Alert>

            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Error Details (Development):
                </Typography>
                <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="caption" component="pre">
                    {error?.toString()}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                disabled={this.state.retryCount >= 3}
              >
                {this.state.retryCount >= 3 ? 'Max Retries Reached' : 'Try Again'}
              </Button>
              
              <Button 
                variant="outlined" 
                onClick={() => window.location.reload()}
              >
                Reload Page
              </Button>
            </Stack>
            
            {this.state.retryCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Retry attempt: {this.state.retryCount}/3
              </Typography>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default EnhancedErrorBoundary;